/**
 * Per-provider configuration for the Quality Checks UI (DEV-2885).
 *
 * The Quality Checks components were written for Proofpoint, with its name and its field set
 * hardcoded. Theta Lake needs the same components with different copy and a smaller field set,
 * so the provider-specific parts live here and the components read them.
 *
 * Proofpoint's entries reproduce its previous hardcoded behaviour exactly, so parameterising is
 * a no-op for it. `provider` defaults to `proofpoint` everywhere, which keeps the two existing
 * mount points (ContentQualityPage.vue and pages/Settings.vue) and the OAuth.vue mixin working
 * with no change at the call site.
 *
 * `statuses` mirrors `ThetalakeIntegration.default_status` / `ProofpointIntegration.default_status`
 * in Core, because this list seeds the *client-side* form for a not-yet-saved check while the
 * server seeds the real rows via PUT /quality-checks/{id}/statuses/initialize. If the two drift,
 * a new check looks different before and after its first save.
 */

// Severity values as the API uses them. PASS/FLAG/FAIL/INCONCLUSIVE/ERROR = 0/1/2/5/6.
// Single source of truth: QualityChecks.vue previously kept its own parallel `Severity` object.
export const SEVERITY = {
    PASS: 0,
    FLAG: 1,
    FAIL: 2,
    INCONCLUSIVE: 5,
    ERROR: 6,
};

export const SEVERITY_LABELS = {
    [SEVERITY.PASS]: 'PASS',
    [SEVERITY.FLAG]: 'FLAG',
    [SEVERITY.FAIL]: 'FAIL',
    [SEVERITY.INCONCLUSIVE]: 'INCONCLUSIVE',
    [SEVERITY.ERROR]: 'ERROR',
};

/** Display label for a severity value. Blank for anything unrecognised, as before. */
export function severityLabel(value) {
    return SEVERITY_LABELS[value] || '';
}

export const QUALITY_CHECK_PROVIDERS = {
    proofpoint: {
        key: 'proofpoint',
        // Name of the integration in the `externalApis` store and in the subscription features
        // map. Same string for both providers today, kept separate because nothing guarantees it.
        apiName: 'proofpoint',
        featureKey: 'proofpoint',
        label: 'Proofpoint',

        // Proofpoint seeds a default check on first view and exposes the "default" flag. Unchanged.
        autoSeedDefaultCheck: true,
        defaultCheckName: 'Proofpoint Default Policy Check',
        showDefaultCheckbox: true,
        defaultCheckLabel: 'Default Proofpoint Check',

        // No cap on how many checks an account can create.
        maxChecks: null,

        // Proofpoint targets a named config on its side, identified by a free-text external ID.
        showConfigExternalId: true,
        configExternalIdLabel: 'Proofpoint Config External API ID',
        configExternalIdHelp:
            'Will use the config set as default for external APIs in Proofpoint if this field is left blank.',

        showSyndicate: true,

        // Proofpoint has no distinct ERROR presentation: the API collapses severity 6 onto the
        // `inconclusive` status key, so the form hides the ERROR row and `save()` copies the
        // INCONCLUSIVE label onto it. Preserved exactly.
        collapseErrorOntoInconclusive: true,

        // `meta` shape for a new check. A factory, not a literal, so no two checks share a nested
        // object — these are v-model targets.
        newCheckMeta: () => ({
            should_send_to_syndicate: false,
            external_ids: {success_flag: null, flag_fail: null},
        }),

        statuses: [
            { severity: SEVERITY.PASS, status_label: 'Proofpoint Awesome', icon_class: 'fa fa-check-circle', tag_class: 'bg-success' },
            { severity: SEVERITY.FLAG, status_label: 'Proofpoint Exception', icon_class: 'fa fa-flag', tag_class: 'bg-warning' },
            { severity: SEVERITY.FAIL, status_label: 'Proofpoint Failed', icon_class: 'fa fa-times-circle', tag_class: 'bg-danger' },
            { severity: SEVERITY.INCONCLUSIVE, status_label: 'Proofpoint Inconclusive', icon_class: 'fa fa-question-circle', tag_class: 'bg-light text-dark' },
            { severity: SEVERITY.ERROR, status_label: 'Proofpoint Inconclusive', icon_class: 'fa fa-question-circle', tag_class: 'bg-light text-dark' },
        ],
    },

    thetalake: {
        key: 'thetalake',
        apiName: 'thetalake',
        featureKey: 'thetalake',
        label: 'Theta Lake',
        autoSeedDefaultCheck: false,
        showDefaultCheckbox: false,

        // One SWRV rule binds one integration to one workflow and one policy on the Theta Lake
        // side, so more than one check here would have nothing distinct to map onto. Enforced in
        // the UI only — the API accepts any number.
        maxChecks: 1,
        showConfigExternalId: false,

        // Syndicate is not part of the Theta Lake flow.
        showSyndicate: false,

        // Theta Lake has no ERROR row at all — it is not seeded server-side and nothing can
        // produce it, because every infrastructure failure maps to FAIL (fail-closed). So there
        // is no severity-6 status to hide, collapse, or edit. Proofpoint keeps its ERROR row and
        // continues to collapse it onto `inconclusive`.
        collapseErrorOntoInconclusive: false,

        // Neither of Proofpoint's meta fields applies: no syndicate step, and no external
        // config ID because policies are configured on Theta Lake's side. Sending them
        // anyway would imply settings the UI never lets anyone set.
        newCheckMeta: () => ({}),

        statuses: [
            { severity: SEVERITY.PASS, status_label: 'Theta Lake Passed', icon_class: 'fa fa-check-circle', tag_class: 'bg-success' },
            { severity: SEVERITY.FLAG, status_label: 'Theta Lake Exception', icon_class: 'fa fa-flag', tag_class: 'bg-warning' },
            { severity: SEVERITY.FAIL, status_label: 'Theta Lake Failed', icon_class: 'fa fa-times-circle', tag_class: 'bg-danger' },
            { severity: SEVERITY.INCONCLUSIVE, status_label: 'Theta Lake Inconclusive', icon_class: 'fa fa-question-circle', tag_class: 'bg-light text-dark' },
            // No ERROR row: severity 6 is unreachable for Theta Lake and is not seeded
            // server-side either, so this list stays in step with what /statuses/initialize
            // creates. Four rows, not five.
        ],
    },
};

export const DEFAULT_QUALITY_CHECK_PROVIDER = 'proofpoint';

/** Resolve a provider config, falling back to Proofpoint for callers that pass nothing. */
export function providerConfig(key) {
    return QUALITY_CHECK_PROVIDERS[key] || QUALITY_CHECK_PROVIDERS[DEFAULT_QUALITY_CHECK_PROVIDER];
}
